import { type JSX } from 'react';
import { useDevTools } from 'src/hooks/useDevTools';
import {
  LearningSessionAction,
  ResponseState,
} from 'src/apps/LearningSession/hooks/learningSessionReducer';
import { Direction, TemplateName } from 'src/api/learningSessions/types';
import { MCResponse } from './MCResponse';
import { TypingResponse } from './TypingResponse';
import { TappingResponse } from './TappingResponse';
import { AudioMCResponse } from './AudioMCResponse';
import { TracingResponse } from './TracingResponse';

export type SupportedTemplateName =
  | TemplateName.MULTIPLE_CHOICE
  | TemplateName.AUDIO_MULTIPLE_CHOICE
  | TemplateName.REVERSED_MULTIPLE_CHOICE
  | TemplateName.TYPING
  | TemplateName.TAPPING
  | TemplateName.TRACING;

interface BaseResponse {
  templateName: SupportedTemplateName;
  direction: Direction; // for analytics
}

export interface MCTestResponse extends BaseResponse {
  templateName: TemplateName.MULTIPLE_CHOICE;
  choices: string[];
  correctChoice: string;
  responseType: 'Text' | 'Audio' | 'Image';
  showBigger: boolean;
}

interface AudioMCTestResponse extends BaseResponse {
  templateName: TemplateName.AUDIO_MULTIPLE_CHOICE;
  choices: string[];
  correctChoice: string;
}

interface RMCTestResponse extends BaseResponse {
  templateName: TemplateName.REVERSED_MULTIPLE_CHOICE;
  choices: string[];
  correctChoice: string;
  responseType: 'Text' | 'Audio' | 'Image';
  showBigger: boolean;
}

interface TypingTestResponse extends BaseResponse {
  templateName: TemplateName.TYPING;
  keyboardChoices: string[];
  answers: string[];
  isCopy: boolean;
  showBigger: boolean;
  label: string;
}

interface TappingTestResponse extends BaseResponse {
  templateName: TemplateName.TAPPING;
  choices: string[];
  answers: string[];
  isCopy: boolean;
  showBigger: boolean;
}

export interface TracingTestResponse extends BaseResponse {
  templateName: TemplateName.TRACING;
  character: string;
  answers: string[];
}

// Response is a reserved name so we have to use 'TestResponse'
export type TestResponse =
  | MCTestResponse
  | AudioMCTestResponse
  | RMCTestResponse
  | TypingTestResponse
  | TappingTestResponse
  | TracingTestResponse;

interface ResponseProps {
  response: TestResponse;
  largeScreenLayout: 'column' | 'grid';
  responseState: ResponseState;
  userAnswer: string;
  learningSessionDispatch: (action: LearningSessionAction) => void;
  onVideo?: boolean;
}

const ResponseDisplay = ({
  response,
  largeScreenLayout,
  responseState,
  userAnswer,
  learningSessionDispatch,
  onVideo = false,
}: ResponseProps): JSX.Element => {
  const devToolState = useDevTools();
  const showCorrectAnswer = devToolState?.showCorrectAnswer || false;
  const showFeedback =
    responseState === 'Correct' ||
    responseState === 'Incorrect' ||
    responseState === 'NearlyCorrect';

  switch (response.templateName) {
    // MC and RMC only differ by data passed in
    case TemplateName.MULTIPLE_CHOICE:
    case TemplateName.REVERSED_MULTIPLE_CHOICE:
      if (response.responseType === 'Audio') {
        return (
          <AudioMCResponse
            responseState={responseState}
            userAnswer={userAnswer}
            choices={response.choices}
            correctChoice={response.correctChoice}
            learningSessionDispatch={learningSessionDispatch}
            showCorrectAnswer={showCorrectAnswer}
          />
        );
      }
      return (
        <MCResponse
          choices={response.choices}
          correctChoice={response.correctChoice}
          largeScreenLayout={largeScreenLayout}
          showFeedback={showFeedback}
          type={response.responseType}
          learningSessionDispatch={learningSessionDispatch}
          showBigger={response.showBigger}
          showCorrectAnswer={showCorrectAnswer}
          onVideo={onVideo}
        />
      );
    /* istanbul ignore next: to be removed in WEBSITE-2541 */
    case TemplateName.AUDIO_MULTIPLE_CHOICE:
      return (
        <AudioMCResponse
          responseState={responseState}
          userAnswer={userAnswer}
          choices={response.choices}
          correctChoice={response.correctChoice}
          learningSessionDispatch={learningSessionDispatch}
          showCorrectAnswer={showCorrectAnswer}
        />
      );
    case TemplateName.TYPING:
      return (
        <TypingResponse
          userAnswer={userAnswer}
          responseState={responseState}
          answers={response.answers}
          letters={response.keyboardChoices}
          learningSessionDispatch={learningSessionDispatch}
          showBigger={response.showBigger}
          isCopy={response.isCopy}
          label={response.label}
          showCorrectAnswer={showCorrectAnswer}
          onVideo={onVideo}
        />
      );
    case TemplateName.TAPPING:
      return (
        <TappingResponse
          responseState={responseState}
          tappingOptions={response.choices}
          learningSessionDispatch={learningSessionDispatch}
          showBigger={response.showBigger}
          isCopy={response.isCopy}
          answers={response.answers}
          showCorrectAnswer={showCorrectAnswer}
          onVideo={onVideo}
        />
      );
    case TemplateName.TRACING:
      return (
        <TracingResponse
          character={response.character}
          responseState={responseState}
          learningSessionDispatch={learningSessionDispatch}
        />
      );
  }
};

export default ResponseDisplay;
